import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useToken } from '../../utils/hooks';
import {
  isInriktning,
  RevisionScope,
  Roll,
  verifieringApi,
} from '../../utils/api';

/** Query-nycklar för revisionen – delade mellan lista, detaljvy och mutationer. */
export const revisionKeys = {
  list: (s: RevisionScope) => ['revision-list', s.kundId, s.ar, s.inriktning],
  revealed: (s: RevisionScope) => [
    'revision-revealed',
    s.kundId,
    s.ar,
    s.inriktning,
  ],
};

/**
 * Tolkar route-parametrarna till revisionens skopning. `null` när URL:en
 * inte är giltig (sidan navigerar då till kundvalet).
 */
export function useRevisionScope(): {
  scope: RevisionScope | null;
  basePath: string;
} {
  const { kundId, ar, inriktning } = useParams();
  const arNumber = Number(ar);
  const giltig =
    !!kundId &&
    Number.isInteger(arNumber) &&
    inriktning !== undefined &&
    isInriktning(inriktning);

  return {
    scope: giltig ? { kundId, ar: arNumber, inriktning } : null,
    basePath: giltig
      ? `/kund/${encodeURIComponent(kundId)}/ar/${arNumber}/${inriktning}/revision`
      : '/',
  };
}

/**
 * Användarens roll för kunden ur /me/access (cache-delad med kundvalet).
 * Styr vilken vy som öppnas: revisionsledaren får listan, revisorer enbart
 * de avpseudonymiserade gårdarna (kravspec v4 C.6).
 */
export function useRollForKund(kundId: string | undefined): {
  roll: Roll | undefined;
  /** Läsuppdrag utan identitet (Ö21) – bara de avpseudonymiserade gårdarna visas. */
  endastPseudonymiserat: boolean;
  /** Kundens namn från uppsättningen; faller tillbaka till kundId. */
  kundNamn: string | undefined;
  /**
   * AS-4231: dataskyddsvillkoren godkända för kunden. False även när kunden
   * saknas i åtkomsterna – sidorna skickar då tillbaka till kundvalet.
   */
  dataskyddGodkant: boolean;
  isPending: boolean;
} {
  const { accessToken } = useToken();
  const { data, isPending } = useQuery({
    queryKey: ['me-access'],
    queryFn: () => verifieringApi.getMyAccess(accessToken!),
    enabled: !!accessToken,
  });
  const kund = data?.kunder.find((k) => k.kundId === kundId);
  return {
    roll: kund?.roll,
    endastPseudonymiserat: kund?.endastPseudonymiserat ?? false,
    kundNamn: kund?.kundNamn ?? kund?.kundId,
    dataskyddGodkant: kund?.dataskyddGodkant ?? false,
    isPending: !accessToken || isPending,
  };
}
